"use client";

import React, { useState, useEffect, useRef } from "react";
import { Cpu, Zap, Database, MessageSquare, Activity, Layers, Network, Mic } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
type EngineLayer = {
  id: string;
  title: string;
  shortDesc: string;
  icon: React.ElementType;
  details: {
    trigger: string;
    content: string;
  }[];
};
const engineLayers: EngineLayer[] = [{
  id: "layer-ingestion",
  title: "Signal Ingestion & Processing",
  shortDesc: "Real-time audio stream normalization and voice activity detection.",
  icon: Mic,
  details: [{
    trigger: "Latency Management",
    content: "Our proprietary jitter buffer and edge-based processing ensure sub-20ms latency for audio packet handling, eliminating the 'awkward pause' typical of VoIP systems."
  }, {
    trigger: "Noise Cancellation",
    content: "Adaptive filtering algorithms isolate human speech from background noise in real-time, ensuring crystal-clear input for the transcription engine."
  }]
}, {
  id: "layer-nlp",
  title: "Neural Understanding Core",
  shortDesc: "Multi-turn intent recognition and semantic analysis.",
  icon: Network,
  details: [{
    trigger: "Intent Classification",
    content: "Utilizing fine-tuned transformer models to classify user intent with 98% accuracy, even in complex, non-linear conversations."
  }, {
    trigger: "Entity Extraction",
    content: "Automatically identifies and structures key data points (dates, names, account numbers) from unstructured speech for immediate CRM syncing."
  }]
}, {
  id: "layer-rag",
  title: "Context & Knowledge Graph",
  shortDesc: "Retrieval-Augmented Generation (RAG) for business-specific accuracy.",
  icon: Database,
  details: [{
    trigger: "Dynamic Context Retrieval",
    content: "The engine queries your specific business knowledge base and CRM history in real-time to provide answers that are factually accurate and personalized."
  }, {
    trigger: "Security & Compliance",
    content: "PII redaction and enterprise-grade encryption ensure that sensitive customer data is handled securely during the retrieval process."
  }]
}, {
  id: "layer-synthesis",
  title: "Generative Synthesis",
  shortDesc: "Human-like response generation and text-to-speech rendering.",
  icon: MessageSquare,
  details: [{
    trigger: "Adaptive Tone Engine",
    content: "The AI adjusts its speaking pace, tone, and empathy levels based on the sentiment analysis of the user's voice."
  }, {
    trigger: "Streamed TTS",
    content: "Response tokens are synthesized into audio streams instantly, allowing the AI to begin speaking before the full sentence is even generated."
  }]
}];
const LayerPlate = ({
  isActive,
  index,
  total
}: {
  isActive: boolean;
  index: number;
  total: number;
}) => {
  const yOffset = (total - 1 - index) * 40;
  const strokeColor = isActive ? "var(--color-primary)" : "var(--color-border)";
  const fillColor = isActive ? "var(--color-primary)" : "var(--color-card)";
  const fillOpacity = isActive ? 0.15 : 0.02;
  const glow = isActive ? "drop-shadow(0 0 15px var(--color-primary))" : "";
  return <g transform={`translate(0, ${yOffset})`} className="transition-all duration-700 ease-out" style={{
    filter: glow
  }}>
      {}
      <path d="M50 30 L350 30 L400 60 L100 60 Z" fill={fillColor} fillOpacity={fillOpacity} stroke={strokeColor} strokeWidth={isActive ? 2 : 1} className="transition-all duration-500" />
      <path d="M100 60 L400 60 L400 80 L100 80 Z" fill={fillColor} fillOpacity={fillOpacity * 1.5} stroke={strokeColor} strokeWidth={isActive ? 2 : 1} className="transition-all duration-500" />
      <path d="M400 60 L350 30 L350 50 L400 80 Z" fill={fillColor} fillOpacity={fillOpacity * 2} stroke={strokeColor} strokeWidth={isActive ? 2 : 1} className="transition-all duration-500" />
      
      {}
      {isActive && <g className="animate-pulse">
           <circle cx="225" cy="45" r="3" fill="var(--color-primary)" />
           <circle cx="250" cy="50" r="2" fill="var(--color-primary)" opacity="0.7" />
           <circle cx="200" cy="40" r="2" fill="var(--color-primary)" opacity="0.7" />
        </g>}
    </g>;
};
export default function ConversationalCore() {
  const [activeLayerId, setActiveLayerId] = useState<string>(engineLayers[0].id);
  const observerRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "-40% 0px -40% 0px",
      threshold: 0
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("data-layer-id");
          if (id) setActiveLayerId(id);
        }
      });
    }, options);
    observerRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  return <section id="conversational-core" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
            <Cpu className="size-4" />
            <span>Architecture Deep Dive</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white">
            The Neural Architecture of <span className="text-primary">MollyCall</span>
          </h2>
          <p className="text-xl text-muted-foreground">
            A transparent look at the low-latency, multi-agent engine that powers our conversational AI.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="hidden lg:block sticky top-32 h-[600px] w-full flex items-center justify-center perspective-1000">
            <div className="relative w-full max-w-[500px] aspect-square">
              {}
              <svg viewBox="0 0 500 500" className="w-full h-full drop-shadow-2xl" style={{
              overflow: 'visible'
            }}>
                <defs>
                  <linearGradient id="glow-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="transparent" />
                  </linearGradient>
                </defs>
                
                {}
                <line x1="250" y1="50" x2="250" y2="450" stroke="var(--color-primary)" strokeWidth="1" strokeDasharray="4 4" opacity="0.3" />

                {}
                {engineLayers.map((layer, i) => <LayerPlate key={layer.id} isActive={activeLayerId === layer.id} index={i} total={engineLayers.length} />)}

                {}
                <circle r="4" fill="white" className="animate-ping opacity-75">
                  <animateMotion dur="3s" repeatCount="indefinite" path="M250 400 L250 100" />
                </circle>
              </svg>

              {}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                {engineLayers.map((layer, i) => {
                const isActive = activeLayerId === layer.id;
                const topPos = 50 + (engineLayers.length - 1 - i) * 40;
                return <div key={`label-${layer.id}`} className={`absolute left-[85%] transition-all duration-500 flex items-center gap-3 ${isActive ? 'opacity-100 translate-x-0' : 'opacity-30 translate-x-4'}`} style={{
                  top: `${topPos}px`
                }}>
                        <div className={`h-[1px] w-8 ${isActive ? 'bg-primary' : 'bg-border'}`}></div>
                        <span className={`text-sm font-mono whitespace-nowrap ${isActive ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                          {layer.title}
                        </span>
                     </div>;
              })}
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-24 pb-24">
            {engineLayers.map((layer, i) => <div key={layer.id} data-layer-id={layer.id} data-index={i} ref={el => {
            observerRefs.current[i] = el;
          }} className={`transition-all duration-500 p-6 rounded-2xl border ${activeLayerId === layer.id ? "bg-card/50 border-primary/50 shadow-[0_0_30px_-10px_var(--color-primary)]" : "bg-card/20 border-border/50 opacity-60 hover:opacity-80"}`}>
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-3 rounded-lg ${activeLayerId === layer.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                    <layer.icon className="size-6" />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold mb-2 ${activeLayerId === layer.id ? "text-white" : "text-muted-foreground"}`}>
                      {layer.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {layer.shortDesc}
                    </p>
                  </div>
                </div>

                {}
                <Accordion type="single" collapsible className="w-full">
                  {layer.details.map((detail, idx) => <AccordionItem key={idx} value={`item-${i}-${idx}`} className="border-border/40">
                      <AccordionTrigger className="text-foreground hover:text-primary hover:no-underline py-4">
                        <span className="flex items-center gap-2">
                          <Activity className="size-4 text-primary/70" />
                          {detail.trigger}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {detail.content}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>)}
            
            {}
            <div className="lg:hidden mt-8 p-8 bg-card/30 rounded-xl border border-border text-center">
              <Layers className="size-12 text-primary mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Full Architecture View</h4>
              <p className="text-muted-foreground mb-6">
                View on desktop to see the interactive exploded engine diagram.
              </p>
              <Button className="w-full sm:w-auto">
                Request Technical Demo
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}